import type { EventQueue } from './event-queue';
import type { UploadEvent } from '../sensors';

/**
 * Convert any legacy queued event (pre-V4, carried absolute `ts`) into
 * the new privacy-preserving format. A legacy queued event becomes a
 * single-event window: fresh eventGuid, tsRelMs=0. The absolute `ts`
 * is dropped. Users with a queue from the old client don't lose their
 * captured data; they just lose the absolute time for that batch
 * (which was the goal anyway).
 */
function ensureUploadShape(raw: unknown): UploadEvent {
  const ev = raw as Record<string, unknown>;
  if (typeof ev.eventGuid === 'string' && typeof ev.tsRelMs === 'number') {
    return ev as unknown as UploadEvent;
  }
  // Legacy event from the offline queue. Drop the absolute `ts`,
  // mint a single-event window. Cast through unknown — runtime
  // shape stays kind-specific (peakG/peakLatG etc untouched).
  const { ts: _legacyTs, ...rest } = ev;
  return {
    ...rest,
    eventGuid: crypto.randomUUID(),
    tsRelMs: 0,
  } as unknown as UploadEvent;
}

/**
 * Drains the local queue in batches and uploads to the backend.
 *
 * Behaviour:
 *  - Runs on a timer (default 30 s) once started
 *  - Skips if navigator.onLine is false (no point waking the radio)
 *  - On HTTP 5xx or network error (fetch throws), re-queues the batch
 *  - On HTTP 4xx (other than 429), DROPS the batch — those events were
 *    invalid per the server, keeping them would loop forever
 *  - On HTTP 429, requeues and waits one more interval
 *  - Reacts to the browser `online` event: triggers an immediate flush
 *  - Reacts to `visibilitychange` (page comes back to foreground): same
 *
 * Crash-safe: if the page is closed while a batch is in flight after
 * being drained, the events are lost (drain is destructive). For v1
 * acceptable — the alternative (peek + mark + commit) doubles every
 * batch's IndexedDB roundtrips. M5+ may revisit with Background Sync.
 */
export interface UploaderOpts {
  queue: EventQueue;
  endpoint?: string;
  batchSize?: number;
  intervalMs?: number;
  onStats?: (s: UploadStats) => void;
}

export interface UploadStats {
  uploaded: number;
  pending: number;
  lastError: string | null;
  lastSuccessTs: number | null;
}

export class Uploader {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight = false;
  private readonly endpoint: string;
  private readonly batchSize: number;
  private readonly intervalMs: number;
  private readonly stats: UploadStats = {
    uploaded: 0,
    pending: 0,
    lastError: null,
    lastSuccessTs: null,
  };
  private readonly onlineHandler = () => void this.flush();
  private readonly visibilityHandler = () => {
    if (!document.hidden) void this.flush();
  };

  constructor(private readonly opts: UploaderOpts) {
    this.endpoint = opts.endpoint ?? '/v1/events';
    this.batchSize = opts.batchSize ?? 500;
    this.intervalMs = opts.intervalMs ?? 30_000;
  }

  start(): void {
    if (this.timer || this.inFlight) {
      // already started — still register listeners idempotently
    }
    window.addEventListener('online', this.onlineHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);
    void this.tick();
  }

  stop(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    window.removeEventListener('online', this.onlineHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  /** Trigger an upload immediately rather than waiting for the next tick. */
  async flush(): Promise<void> {
    if (this.inFlight) return;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    await this.tick(true);
  }

  private async tick(isManualFlush = false): Promise<void> {
    this.timer = null;
    this.inFlight = true;
    let batch: ReturnType<EventQueue['drain']> extends Promise<infer T> ? T : never = [] as never;
    try {
      if (!navigator.onLine) {
        await this.refreshPending();
        return;
      }
      batch = await this.opts.queue.drain(this.batchSize);
      if (batch.length === 0) {
        await this.refreshPending();
        return;
      }

      // Normalise: any pre-V4 events in the queue become single-event
      // windows. New events pass through untouched.
      const wirePayload = batch.map(ensureUploadShape);

      let outcome: 'success' | 'drop' | 'retry' = 'retry';
      try {
        const r = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(wirePayload),
        });
        if (r.ok) {
          outcome = 'success';
          this.stats.uploaded += batch.length;
          this.stats.lastError = null;
          this.stats.lastSuccessTs = Date.now();
        } else if (r.status >= 400 && r.status < 500 && r.status !== 429) {
          outcome = 'drop';
          this.stats.lastError = `server rejected ${batch.length} events (HTTP ${r.status})`;
        } else {
          outcome = 'retry';
          this.stats.lastError = `server ${r.status}; will retry`;
        }
      } catch (err) {
        // Network error, DNS failure, TLS error, offline transition mid-fetch...
        outcome = 'retry';
        this.stats.lastError = err instanceof Error ? err.message : String(err);
      }

      if (outcome === 'retry') {
        // Put the events back. Never lose data on a transient failure —
        // this is the whole point of having a queue.
        await this.opts.queue.requeue(batch);
      }
    } catch (err) {
      // drain() itself failed — IndexedDB issue. Don't reschedule wild.
      this.stats.lastError = err instanceof Error ? err.message : String(err);
    } finally {
      this.inFlight = false;
      await this.refreshPending();
      if (!isManualFlush) {
        this.timer = setTimeout(() => void this.tick(), this.intervalMs);
      }
    }
  }

  private async refreshPending(): Promise<void> {
    try {
      this.stats.pending = await this.opts.queue.size();
    } catch {
      // ignore
    }
    this.opts.onStats?.({ ...this.stats });
  }
}
